import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import LoadingSpinner from '../../components/LoadingSpinner';
import { updateSector } from '../../store/actions';
import useTitle from '../../hooks/useTitle';

const AdminProjectCategoryEdit = () => {
    const { data: sectorsWithProjects, loading, error } = useSelector(state => state.sectorsWithProjects);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Задаем название сайта
    useTitle('Редактирование категории проекта | Админ-панель');

    // Извлечение названия категории из пути
    const params = useParams(); 
    const categoryName = params.categoryName;

    // Локальное состояние формы
    const [formData, setFormData] = useState({
        id: 0,
        name: '',
        title: '',
        displayOrder: 0,
        projects: []
    });

    // Состояние для ошибок валидации
    const [validationErrors, setValidationErrors] = useState({
        title: '',
        projects: ''
    });

    // Находим выбранную категорию при загрузке компонента
    useEffect(() => {
        if (sectorsWithProjects && sectorsWithProjects.length > 0) {
            const currentSector = sectorsWithProjects.find(s => s.name === categoryName);
            if (currentSector) {
                setFormData({
                    id: currentSector.id,
                    name: currentSector.name,
                    title: currentSector.title,
                    displayOrder: currentSector.displayOrder,
                    projects: currentSector.projects.map(p => ({
                        id: p.id,
                        projectName: p.projectName,
                        displayOrder: p.displayOrder
                    }))
                });
            }
        }
    }, [sectorsWithProjects, categoryName]);

    // Валидация названия категории
    const validateTitle = (title) => {
        const trimmedTitle = title.trim();
        
        if (trimmedTitle.length < 2) {
            return 'Название категории должно содержать не менее 2 символов';
        }
        
        if (trimmedTitle.length > 100) {
            return 'Название категории не должно превышать 100 символов';
        }
        
        // Проверка на уникальность названия
        if (sectorsWithProjects) {
            const existingSector = sectorsWithProjects.find(
                s => s.title.toLowerCase() === trimmedTitle.toLowerCase() && s.id !== formData.id
            );
            
            if (existingSector) {
                return 'Категория с таким названием уже существует';
            }
        }
        
        return '';
    };

    // Валидация списка проектов
    const validateProjects = (projects) => {
        // Проверяем количество строк
        if (projects.length > 1000) {
            return 'Список не может содержать более 1000 учреждений';
        }
        
        // Проверяем каждую строку
        for (let i = 0; i < projects.length; i++) {
            const project = projects[i].projectName.trim();
            
            // Пропускаем пустые строки
            if (project === '') continue;
            
            if (project.length < 2) {
                return `Строка ${i + 1}: Название учреждения должно содержать не менее 2 символов`;
            }
            
            if (project.length > 200) {
                return `Строка ${i + 1}: Название учреждения не должно превышать 200 символов`;
            }
        }
        
        return '';
    };

    // Обработчик изменения названия категории
    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setFormData({
            ...formData,
            title: newTitle
        });
        
        // Валидация в реальном времени
        setValidationErrors({
            ...validationErrors,
            title: validateTitle(newTitle)
        });
    };

    // Обработчик изменения списка проектов
    const handleProjectsChange = (e) => {
        // Разбиваем текст на строки
        const lines = e.target.value.split('\n');
        
        // Создаем новый массив проектов
        const updatedProjects = lines.map((line, index) => ({
            id: index < formData.projects.length ? formData.projects[index].id : 0,
            projectName: line,
            displayOrder: index
        }));
        
        setFormData({
            ...formData,
            projects: updatedProjects
        });
        
        // Валидация в реальном времени
        setValidationErrors({
            ...validationErrors,
            projects: validateProjects(updatedProjects)
        });
    };

    // Отправка формы
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Финальная валидация перед отправкой
        const titleError = validateTitle(formData.title);
        const projectsError = validateProjects(formData.projects);
        
        if (titleError || projectsError) {
            setValidationErrors({
                title: titleError,
                projects: projectsError
            });
            return;
        }

        // Фильтруем пустые строки перед отправкой
        const filteredProjects = formData.projects
            .filter(p => p.projectName.trim() !== '')
            .map((p, index) => ({
                ...p,
                projectName: p.projectName.trim(),
                displayOrder: index
            }));

        const result = await dispatch(updateSector(
            formData.id, 
            {
                name: formData.name,
                title: formData.title,
                displayOrder: formData.displayOrder,
                projects: filteredProjects
            }
        ));

        if (result.success) {
            navigate('/performance-admin-panel/project-categories');
        }
    };

    // Отмена и возврат к списку категорий
    const handleCancel = () => {
        navigate('/performance-admin-panel/project-categories');
    };

    return (
        <Container className="py-4">
            <h2 className="mb-4">Редактирование категории «{formData.title}»</h2>
            {loading ? 
                <div className="spinner-container">
                    <LoadingSpinner message="Загрузка информации о проектах..."/>
                </div> :
                error ? 
                    <div className="alert alert-danger">
                        {error}
                    </div> 
                : formData.id !== 0 ? (
                    <Form onSubmit={handleSubmit}>
                        <div className="mb-5">
                            {/* <Form.Group className="mb-4">
                                <Form.Label>Название категории</Form.Label>
                                <Form.Control 
                                    type="text"
                                    value={formData.title}
                                    onChange={handleTitleChange}
                                    isInvalid={!!validationErrors.title}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {validationErrors.title}
                                </Form.Control.Feedback>
                                <Form.Text className="text-muted">
                                    От 2 до 100 символов, должно быть уникальным
                                </Form.Text>
                            </Form.Group> */}
                            
                            <Form.Group className="mb-4">
                                <Form.Label>Список учреждений</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    value={formData.projects.map(p => p.projectName).join('\n')}
                                    onChange={handleProjectsChange}
                                    isInvalid={!!validationErrors.projects}
                                    placeholder="Введите по одному учреждению на строку"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {validationErrors.projects}
                                </Form.Control.Feedback>
                                <Form.Text className="text-muted">
                                    Каждое название от 2 до 200 символов, не более 1000 строк
                                </Form.Text>
                            </Form.Group>
                             
                            <Row className="mt-4">
                                <Col>
                                    <div className="d-flex justify-content-center">
                                        <Button 
                                            variant="secondary"
                                            className="me-2 px-5"
                                            onClick={handleCancel}
                                        >
                                            Отмена
                                        </Button>
                                        <Button 
                                            type="submit" 
                                            variant="primary"
                                            className="px-5"
                                            disabled={!!validationErrors.title || !!validationErrors.projects}
                                        >
                                            Сохранить
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Form>
                ) : (
                    <div className="alert alert-warning">
                        Категория не найдена
                    </div>
                )
            }
        </Container>
    );
};

export default AdminProjectCategoryEdit;